import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Search, MessageCircle, Mail, Smartphone, ContactRound } from 'lucide-react';
import { money, fmtDate, waLink, STATE_BADGE, statementText } from '../lib/share';
import ClientCard from '../components/ClientCard';
import DocShare from '../components/DocShare';

const Stat = ({ label, value, cls = 'text-[#022C22]' }) => (
  <div className="bg-[#F8F9FA] rounded-lg p-3"><p className="text-[11px] text-[#64748B]">{label}</p><p className={`font-heading font-black text-lg ${cls}`}>{value}</p></div>
);

const Detail = ({ st }) => {
  const c = st.client; const s = st.summary;
  const shareEmail = async () => {
    try { const r = (await api.crmShareEmail(c.client_id)).data; toast.success(`SalesPRO: Estado de cuenta enviado a ${r.to}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error al enviar correo'); }
  };
  return (
    <div className="space-y-4" data-testid="crm-detail">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-heading text-2xl font-black text-[#022C22]">{c.name}</h2>
          <p className="text-sm text-[#64748B]">C.I. {c.cedula_rif} · {c.phone} · {c.email} · {c.company_name}</p>
        </div>
        <div className="flex gap-2">
          <a href={waLink(c.phone, statementText(st))} target="_blank" rel="noreferrer" data-testid="crm-share-whatsapp"><Button className="bg-green-600 hover:bg-green-700"><MessageCircle size={14} className="mr-1" /> WhatsApp</Button></a>
          <Button variant="outline" onClick={shareEmail} data-testid="crm-share-email"><Mail size={14} className="mr-1" /> Enviar por correo</Button>
          {c.user_id && <Button variant="outline" data-testid="crm-reset-password" onClick={async () => { try { const r = (await api.resetUserPassword(c.user_id)).data; toast.success(`SalesPRO: Clave temporal de ${r.email}: ${r.temp_password} (deberá cambiarla al entrar)`, { duration: 20000 }); } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); } }}>Regenerar clave</Button>}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        <Stat label="Total comprado" value={money(s.total_bought)} /><Stat label="Pagado" value={money(s.total_paid)} cls="text-green-700" />
        <Stat label="Saldo" value={money(s.balance)} cls="text-red-600" /><Stat label="Cuotas pagadas" value={s.installments_paid} />
        <Stat label="Cuotas pendientes" value={s.installments_pending} cls="text-amber-700" /><Stat label="Vencidas" value={`${s.installments_overdue} · ${money(s.overdue_amount)}`} cls="text-red-600" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card><CardContent className="p-4">
          <h3 className="font-bold text-[#022C22] mb-2 flex items-center gap-1"><Smartphone size={16} /> Equipos comprados</h3>
          {st.devices.length === 0 ? <p className="text-xs text-[#94A3B8]">Sin compras.</p> : st.devices.map((d, i) => (
            <div key={i} className="flex justify-between text-sm border-b border-[#F1F5F9] py-1"><span>{d.name} ×{d.quantity}</span><span className="text-[#64748B]">{d.invoice_number} · {fmtDate(d.date)}</span></div>))}
          <h3 className="font-bold text-[#022C22] mt-4 mb-2">Recibos de venta</h3>
          {st.sales.map((s) => <div key={s.sale_id} className="flex justify-between items-center text-sm border-b border-[#F1F5F9] py-1"><span>{s.invoice_number}</span><DocShare kind="sale" refId={s.sale_id} phone={c.phone} label="Recibo" compact /></div>)}
          <h3 className="font-bold text-[#022C22] mt-4 mb-2">Pagos recibidos</h3>
          {st.payments.length === 0 ? <p className="text-xs text-[#94A3B8]">Sin pagos registrados.</p> : st.payments.map((p) => (
            <div key={p.payment_id} className="flex justify-between text-sm border-b border-[#F1F5F9] py-1"><span>{fmtDate(p.created_at)} · {p.notes || p.method_name || 'Pago'}</span><span className="flex items-center gap-2"><span className="font-semibold text-green-700">{money(p.amount)}</span><DocShare kind="payment" refId={p.payment_id} phone={c.phone} label="Ticket" compact /></span></div>))}
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <h3 className="font-bold text-[#022C22] mb-2">Cuotas (fechas y montos)</h3>
          <div className="max-h-80 overflow-y-auto">{st.installments.length === 0 ? <p className="text-xs text-[#94A3B8]">Sin planes de financiamiento.</p> : st.installments.map((i) => (
            <div key={i.installment_id} className="flex justify-between items-center text-sm border-b border-[#F1F5F9] py-1.5" data-testid={`crm-inst-${i.number}`}>
              <span>{i.invoice_number} · #{i.number}/{i.total_installments} · {fmtDate(i.due_date)}</span>
              <span className="flex items-center gap-2">{money(i.amount)} <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATE_BADGE[i.state]}`}>{i.state}</span></span>
            </div>))}</div>
        </CardContent></Card>
      </div>
      <ClientCard client={c} />
    </div>
  );
};

export default function CRM() {
  const [q, setQ] = useState('');
  const [list, setList] = useState([]);
  const [st, setSt] = useState(null);
  const search = async () => { try { setList((await api.crmSearch({ q })).data); } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); } };
  useEffect(() => { search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const open = async (id) => { try { setSt((await api.crmDetail(id)).data); } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); } };
  return (
    <div className="space-y-4" data-testid="crm-page">
      <h1 className="font-heading text-3xl font-black text-[#022C22] flex items-center gap-2"><ContactRound /> CRM de Clientes</h1>
      <div className="flex gap-2 max-w-xl">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} placeholder="Buscar por nombre, cédula, teléfono o correo" data-testid="crm-search-input" />
        <Button onClick={search} data-testid="crm-search-btn"><Search size={16} /></Button>
      </div>
      <div className="grid lg:grid-cols-[340px_1fr] gap-4">
        <div className="space-y-2 max-h-[75vh] overflow-y-auto">
          {list.map((c) => (
            <button key={c.client_id} onClick={() => open(c.client_id)} data-testid={`crm-client-${c.client_id}`}
              className={`w-full text-left p-3 rounded-lg border ${st?.client?.client_id === c.client_id ? 'border-primary bg-[#F0FBF7]' : 'border-[#E2E8F0] bg-white'} hover:border-primary transition-colors`}>
              <p className="font-semibold text-[#022C22]">{c.name}</p>
              <p className="text-xs text-[#64748B]">C.I. {c.cedula_rif} · {c.company_name}</p>
              <p className="text-xs mt-1">Saldo <b>{money(c.summary.balance)}</b> · {c.summary.pending} pend. {c.summary.overdue > 0 && <span className="text-red-600 font-semibold">· {c.summary.overdue} vencida(s)</span>}</p>
            </button>))}
          {list.length === 0 && <p className="text-sm text-[#94A3B8]">Sin resultados.</p>}
        </div>
        <div>{st ? <Detail st={st} /> : <p className="text-sm text-[#94A3B8] py-10 text-center">Selecciona un cliente para ver sus cuotas, pagos y equipos.</p>}</div>
      </div>
    </div>
  );
}
